import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Building2, Save } from 'lucide-react'
import { api } from '@/util/api'
import { AdminLayout } from '@/components/admin/AdminLayout'
import { OrganizationFeatureFlagsPanel } from '@/components/admin/OrganizationFeatureFlagsPanel'
import {
  BackLink,
  Badge,
  Button,
  Card,
  Checkbox,
  Notice,
  Select,
  Skeleton,
  SkeletonGroup,
  TextField,
} from '@/components/play'
import { useAuth } from '@/state/AuthContext'
import { canAccessPlatformAdmin } from '@/lib/adminCapabilities'
import type { OrgResource } from '@/types/admin'

const planItems = [
  { value: 'free', label: 'Free' },
  { value: 'starter', label: 'Starter' },
  { value: 'pro', label: 'Pro' },
  { value: 'enterprise', label: 'Enterprise' },
]

export function AdminOrganizationDetailPage() {
  const { organizationId } = useParams()
  const { user, organization: currentOrganization } = useAuth()
  const isPlatformAdmin = canAccessPlatformAdmin(user?.attributes?.role)
  const targetOrganizationId = organizationId || currentOrganization?.id
  const [organization, setOrganization] = useState<OrgResource | null>(null)
  const [name, setName] = useState('')
  const [subscriptionPlan, setSubscriptionPlan] = useState('free')
  const [active, setActive] = useState(true)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)

  useEffect(() => {
    if (!targetOrganizationId) {
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)
    api.getAdminOrganization(targetOrganizationId)
      .then((res) => {
        setOrganization(res.data)
        setName(res.data.attributes.name)
        setSubscriptionPlan(res.data.attributes.subscription_plan || 'free')
        setActive(res.data.attributes.active)
      })
      .catch((err: any) => {
        setError(err?.response?.data?.error?.message || 'Could not load organization.')
      })
      .finally(() => setLoading(false))
  }, [targetOrganizationId])

  async function saveAccount() {
    if (!organization) return

    setSaving(true)
    setSaved(false)
    setError(null)

    try {
      const payload = isPlatformAdmin
        ? { name, subscription_plan: subscriptionPlan, active }
        : { name }
      const res = await api.updateAdminOrganization(organization.id, payload)
      setOrganization(res.data)
      setName(res.data.attributes.name)
      setSubscriptionPlan(res.data.attributes.subscription_plan)
      setActive(res.data.attributes.active)
      setSaved(true)
    } catch (err: any) {
      setError(err?.response?.data?.error?.message || 'Could not save organization.')
    } finally {
      setSaving(false)
    }
  }

  function handleFeatureFlagChange(updatedOrganization: OrgResource) {
    setOrganization(updatedOrganization)
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {isPlatformAdmin && (
          <BackLink to="/admin/organizations">Organizations</BackLink>
        )}

        <div className="flex flex-col justify-between gap-3 sm:flex-row sm:items-center">
          <div>
            <h1 className="flex items-center gap-2 text-3xl font-semibold">
              <Building2 className="shrink-0 text-neutral-10" aria-hidden="true" />
              {organization?.attributes.name || 'Account'}
            </h1>
            <p className="mt-1 text-sm text-neutral-7">
              {isPlatformAdmin ? 'Account controls and platform flags.' : 'Organization settings for your account.'}
            </p>
          </div>
          {organization && (
            <Badge tone={organization.attributes.active ? 'neutral' : 'quiet'}>
              {organization.attributes.active ? 'Active' : 'Inactive'}
            </Badge>
          )}
        </div>

        {error && (
          <Notice tone="error" alert>{error}</Notice>
        )}

        {loading ? (
          <SkeletonGroup label="Loading organization details" className="grid gap-4">
            <Skeleton shape="panel" className="h-40" />
            <Skeleton shape="panel" className="h-32" />
          </SkeletonGroup>
        ) : !targetOrganizationId ? (
          <Notice>No organization is attached to this account.</Notice>
        ) : organization ? (
          <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_360px]">
            <Card title="Account">
              <div className="space-y-4">
                <TextField
                  label="Name"
                  value={name}
                  onChange={(event) => {
                    setName(event.target.value)
                    setSaved(false)
                  }}
                />

                {isPlatformAdmin && (
                  <div className="grid gap-4 sm:grid-cols-2">
                    <Select
                      label="Plan"
                      placeholder="Plan"
                      items={planItems}
                      value={subscriptionPlan}
                      onValueChange={(v) => {
                        setSubscriptionPlan(v ?? 'free')
                        setSaved(false)
                      }}
                    />

                    <Checkbox
                      className="sm:self-end"
                      label="Active account"
                      checked={active}
                      onCheckedChange={(checked) => {
                        setActive(checked)
                        setSaved(false)
                      }}
                    />
                  </div>
                )}

                <div className="flex flex-wrap items-center justify-between gap-3">
                  <p className="text-xs text-neutral-7">
                    Slug <span className="font-data">{organization.attributes.slug}</span>
                  </p>
                  <div className="flex items-center gap-3">
                    {/* Announced when it appears; cleared by the next edit. */}
                    <p role="status" className="text-xs text-neutral-7">{saved ? 'Saved' : ''}</p>
                    <Button onClick={saveAccount} loading={saving} disabled={name.trim().length === 0}>
                      {!saving && <Save />}
                      Save
                    </Button>
                  </div>
                </div>
              </div>
            </Card>

            {isPlatformAdmin ? (
              <OrganizationFeatureFlagsPanel organization={organization} onChange={handleFeatureFlagChange} />
            ) : (
              <Card title="Scope">
                <p className="text-neutral-7">
                  Users and teams in this Admin section are limited to {organization.attributes.name}.
                </p>
              </Card>
            )}
          </div>
        ) : null}
      </div>
    </AdminLayout>
  )
}
